// What the hub calls someone on a screen. Pure: no React, so a test can import it as it is.
//
// The database never keeps a title as a first name (private.first_name_from, migration 20261010203000: "Mr S
// Carter" is Mr Carter, "Mr and Mrs Carter" is Mr Carter too, "Mrs Jane Smith" is Jane). When a buyer's name and
// email give no name at all, the first name is "there", so the hub's emails can say "Hi there,". On a screen "there"
// is no name: the Library says "Evening" on its own, and staff screens use the full name instead (10 Oct).

// The titles the database drops, alone or with marks after them ("Mr", "Dr.", "Lady,"), or written against the next
// word with anything but a letter, a digit, an apostrophe or a hyphen ("Dr.Jones", "Mr/Mrs", "Mr,Carter"), as
// private.guard_people's v_title has them; "Lord-Smith" and "Drew" aren't titles
const TITLES = 'mr|mrs|ms|miss|mx|dr|prof|sir|rev|lady|lord'
const TITLE = new RegExp(`^(${TITLES})([^\\p{L}\\p{N}]*$|[^\\p{L}\\p{N}\\s'’-]+\\p{L})`, 'iu')
const BARE_TITLE = new RegExp(`^(${TITLES})[^\\p{L}\\p{N}]*$`, 'iu')

const tidy = s => String(s ?? '').trim().replace(/\s+/g, ' ')

/** A first word that is a title: "Mr", "mrs.", "Dr.Jones", "Mr/Mrs" */
export const isTitle = word => TITLE.test(tidy(word))

/** "there", the database's stand-in when a name gave none */
export const isStandIn = first => tidy(first).toLowerCase() === 'there'

/**
 * The first name to greet someone by, or '' when there's none: blank, the stand-in "there", or a bare title (which
 * migration 20261010203000 corrects in the database, so only a row from before it could still hold one).
 */
export function realFirstName(first) {
  const s = tidy(first)
  if (!s || isStandIn(s) || BARE_TITLE.test(s)) return ''
  return s
}

/**
 * The name staff screens use for someone: their first name; for "there" or a bare title, their full name; with no
 * first name at all, the first word of their full name, as before, unless it's a title, when it's the full name. ''
 * when there's nothing.
 */
export function staffFirstName(person) {
  const first = realFirstName(person?.first_name)
  if (first) return first
  const full = tidy(person?.full_name)
  if (tidy(person?.first_name)) return full
  const word = full.split(' ')[0] || ''
  return isTitle(word) ? full : word
}

/** Admin's "View as Sam", "View as Mr S Carter" for someone with no first name of their own, else "View as member" */
export function viewAsLabel(person) {
  const name = staffFirstName(person)
  return name ? `View as ${name}` : 'View as member'
}

/** The Library's greeting: "Morning, Sam"; "Morning" on its own when there's no first name to greet by */
export function greetingLine(word, first) {
  const name = realFirstName(first)
  return name ? `${word}, ${name}` : word
}
